import { z } from 'zod';

/**
 * Hybrid Logical Clock per Kulkarni et al. (2014). Carries a physical
 * timestamp (`wall_ms`) and a logical counter (`logical`) that breaks ties
 * when wall-clock time stalls or two events share a millisecond.
 *
 * Total order: lexicographic on (wall_ms, logical). HLC of the server
 * boundary is the canonical ordering of accepted ops within a Workspace
 * (02-architecture.md §4.1).
 */
export interface Hlc {
  wall_ms: number;
  logical: number;
}

export const HlcSchema = z
  .object({
    wall_ms: z.number().int().nonnegative(),
    logical: z.number().int().nonnegative(),
  })
  .strict();

/** Total-order comparator. Returns -1 / 0 / 1. */
export function compareHlc(a: Hlc, b: Hlc): -1 | 0 | 1 {
  if (a.wall_ms < b.wall_ms) return -1;
  if (a.wall_ms > b.wall_ms) return 1;
  if (a.logical < b.logical) return -1;
  if (a.logical > b.logical) return 1;
  return 0;
}

/**
 * A stateful HLC arbiter. One instance lives inside each Workspace DO on
 * the server (the canonical clock) and inside each client (the provisional
 * clock used while a write is queued).
 *
 * `now()` and `merge()` accept an optional `wall_ms` override so tests can
 * supply a deterministic clock; production paths use `Date.now()`.
 */
export class HlcClock {
  private last: Hlc = { wall_ms: 0, logical: 0 };

  /** Current value without advancing. */
  peek(): Hlc {
    return { ...this.last };
  }

  /**
   * Stamp a local event. Monotonically non-decreasing: the next stamp is
   * strictly greater than the previous one in the (wall_ms, logical) order.
   */
  now(wall_ms: number = Date.now()): Hlc {
    if (wall_ms > this.last.wall_ms) {
      this.last = { wall_ms, logical: 0 };
    } else {
      this.last = { wall_ms: this.last.wall_ms, logical: this.last.logical + 1 };
    }
    return { ...this.last };
  }

  /**
   * Incorporate a remote HLC seen on an inbound message. Advances `last`
   * to be strictly greater than both `last` and `remote` in the total order.
   */
  merge(remote: Hlc, wall_ms: number = Date.now()): Hlc {
    const maxWall = Math.max(wall_ms, this.last.wall_ms, remote.wall_ms);
    let logical: number;
    if (maxWall === this.last.wall_ms && maxWall === remote.wall_ms) {
      logical = Math.max(this.last.logical, remote.logical) + 1;
    } else if (maxWall === this.last.wall_ms) {
      logical = this.last.logical + 1;
    } else if (maxWall === remote.wall_ms) {
      logical = remote.logical + 1;
    } else {
      logical = 0;
    }
    this.last = { wall_ms: maxWall, logical };
    return { ...this.last };
  }
}
